import { ConditionParams } from "@multiweb/clients/dataReader";
import { EMPTY_ARRAY } from "@multiweb/reactUtils";
import { IPublicAttributeInfo } from "@multiweb/types/contracts";
import { IOrderBy, IWorkflowMenuItem } from "@multiweb/types/multiweb";
import { ReactAction, RecordType, RootNodeTypedObject } from "@multiweb/types/react";
import {
    CHECK_ALL_RECORDS,
    DELETE_DATASOURCE,
    DESELECT_ALL_RECORDS,
    EXECUTE_WORKFLOW_ACTION,
    GET_WORKFLOW_ACTIONS_ERROR,
    GET_WORKFLOW_ACTIONS_REQUEST,
    GET_WORKFLOW_ACTIONS_SUCCESS,
    INIT_DATASOURCE,
    LOAD_ATTRIBUTE_INFO,
    LOAD_ATTRIBUTE_INFO_ERROR,
    LOAD_ATTRIBUTE_INFO_SUCCESS,
    LOAD_DATA,
    LOAD_DATA_ERROR,
    LOAD_DATA_SUCCESS,
    REFRESH_DATA,
    REFRESH_DATA_ERROR,
    REFRESH_DATA_SUCCESS,
    SELECT_NEXT_RECORD,
    SELECT_PREVIOUS_RECORD,
    SELECT_RECORD,
    SELECT_RECORDS,
    SET_ATTRIBUTES,
    SET_DATASOURCE_MULTISELECT_MODE,
    SET_DISABLE_LOADING,
    SET_HAS_DETAIL_FORM,
    SET_LAST_SELECTED_RECORD_ID,
    SET_ORDER_BY,
    SET_ROOT_NODES,
    TOGGLE_CHECK_ALL_RECORDS,
    TOGGLE_CHECK_ON_SELECTED_RECORDS,
    TOGGLE_ORDER_BY,
    TOGGLE_SELECTED_RECORD_CHECK,
    UNCHECK_ALL_RECORDS,
    UPDATE_FULLTEXT_CONDITION,
    UPDATE_MASTER_FRAME_CONDITION,
    UPDATE_RECORD_ACTIONS,
    UPDATE_RECORD_ACTIONS_SUCCESS,
} from "./constants";
import { DATA_SOURCE_INIT_STATE } from "./initState";
import {
    ICheckAllRecordsAction,
    IDataSourceAttribute,
    IDataSourceConditions,
    IDataSourceInitConfig,
    IDeleteDataSourceAction,
    IDeselectAllRecordsAction,
    IExecuteWorkflowAction,
    IFulltextSearchCondition,
    IInitDataSourceAction,
    ILoadAttributeInfoAction,
    ILoadAttributeInfoErrorAction,
    ILoadAttributeInfoSuccessAction,
    ILoadDataAction,
    ILoadDataErrorAction,
    ILoadDataSuccessAction,
    ILoadWorkflowActionsAction,
    ILoadWorkflowActionsErrorAction,
    ILoadWorkflowActionsSuccessAction,
    IRefreshDataAction,
    IRefreshDataErrorAction,
    IRefreshDataSuccessAction,
    ISelectNextRecord,
    ISelectRecordAction,
    ISelectRecordsAction,
    ISetAttributesAction,
    ISetDatasourceMultiselectModeAction,
    ISetDisableLoadingAction,
    ISetHasDetailFormAction,
    ISetLastSelectedRecordIdAction,
    ISetOrderByAction,
    ISetRootNodes,
    IToggleCheckAllRecordsAction,
    IToggleCheckOnSelectedRecordsAction,
    IToggleOrderByAction,
    IToggleSelectedRecordCheckAction,
    IUncheckAllRecordsAction,
    IUpdateFulltextDataSourceCondition,
    IUpdateMasterFrameDataSourceCondition,
    IUpdateRecordActionsAction,
    IUpdateRecordActionsSuccessAction,
} from "./types";

/**
 * Akce pro inicializaci DataSource.
 *
 * @param dataSourceKey Unikátní klíč pro DataSource.
 * @param config Počáteční inicializace DataSource.
 * @param hasDetailForm Značí, zda je povoleno otevřít detail záznamu.
 * @param orderBy Attributy, podle kterých se řadí data.
 * @param conditions Podmínky pro načítání dat.
 * @param attributeInfo Serializovaná data, vhodné např. pokud dataSource není napojen na server.
 */
export const createInitDataSourceAction = (
    dataSourceKey: string,
    config: IDataSourceInitConfig,
    hasDetailForm: boolean,
    orderBy: ReadonlyArray<IOrderBy> = DATA_SOURCE_INIT_STATE.orderBy,
    conditions: Partial<IDataSourceConditions> = DATA_SOURCE_INIT_STATE.conditions,
    attributeInfo: ReadonlyArray<IPublicAttributeInfo> = EMPTY_ARRAY,
): IInitDataSourceAction => ({
    type: INIT_DATASOURCE,
    payload: {
        config,
        hasDetailForm,
        orderBy,
        conditions,
        attributeInfo,
    },
    meta: {
        dataSourceKey,
    },
});

/**
 * Akce pro smazání DataSource.
 *
 * @param dataSourceKey Unikátní klíč pro DataSource.
 */
export const createDeleteDataSourceAction = (dataSourceKey: string): IDeleteDataSourceAction => ({
    type: DELETE_DATASOURCE,
    meta: {
        dataSourceKey,
    },
});

export const createLoadAttributeInfoAction = (
    dataSourceKey: string,
    attributesNames: ReadonlyArray<string>,
): ILoadAttributeInfoAction => ({
    type: LOAD_ATTRIBUTE_INFO,
    payload: {
        attributesNames,
    },
    meta: {
        dataSourceKey,
    },
});

export const createLoadAttributeInfoSuccessAction = (
    dataSourceKey: string,
    attributeInfo: ReadonlyArray<IPublicAttributeInfo>,
): ILoadAttributeInfoSuccessAction => ({
    type: LOAD_ATTRIBUTE_INFO_SUCCESS,
    payload: {
        attributeInfo,
    },
    meta: {
        dataSourceKey,
    },
});

export const createLoadAttributeInfoErrorAction = (
    dataSourceKey: string,
    message?: string,
): ILoadAttributeInfoErrorAction => ({
    type: LOAD_ATTRIBUTE_INFO_ERROR,
    payload: {
        message,
    },
    meta: {
        dataSourceKey,
    },
});

/**
 * Akce pro načtení nových dat do předem nainicializovaného DataSource.
 *
 * @param dataSourceKey Klíč pro identifikaci DataSource zadaný při inicializaci.
 * @param fetchCount Počet nově načtených řádků.
 */
export const createLoadDataAction = (dataSourceKey: string, fetchCount: number = -1): ILoadDataAction => ({
    type: LOAD_DATA,
    payload: {
        fetchCount,
    },
    meta: {
        dataSourceKey,
    },
});

/**
 * Akce pro potvrzení úspěšně provedeného načítání z MultiWebu.
 *
 * @param dataSourceKey Klíč pro identifikaci DataSource zadaný při inicializaci.
 * @param data Načtená data.
 */
export const createLoadDataSuccessAction = (
    dataSourceKey: string,
    data: ReadonlyArray<RecordType>,
): ILoadDataSuccessAction => ({
    type: LOAD_DATA_SUCCESS,
    payload: {
        data,
    },
    meta: {
        dataSourceKey,
    },
});

/**
 * Akce pro oznámení chybného načtení dat.
 *
 * @param dataSourceKey Klíč pro identifikaci DataSource zadaný při inicializaci.
 * @param message Zpráva popisující chybu.
 */
export const createLoadDataErrorAction = (dataSourceKey: string, message?: string): ILoadDataErrorAction => ({
    type: LOAD_DATA_ERROR,
    payload: {
        message,
    },
    meta: {
        dataSourceKey,
    },
});

/**
 * Akce pro aktualizaci dat v DataSource.
 *
 * @param dataSourceKey Klíč pro identifikaci DataSource zadaný při inicializaci.
 */
export const createRefreshAction = (dataSourceKey: string): IRefreshDataAction => ({
    type: REFRESH_DATA,
    meta: {
        dataSourceKey,
    },
});

/**
 * Akce pro potvrzení úspěšně provedené aktualizace z MultiWebu.
 *
 * @param dataSourceKey Klíč pro identifikaci DataSource zadaný při inicializaci.
 * @param data Načtená data.
 */
export const createRefreshSuccessAction = (
    dataSourceKey: string,
    data: ReadonlyArray<RecordType>,
): IRefreshDataSuccessAction => ({
    type: REFRESH_DATA_SUCCESS,
    payload: {
        data,
    },
    meta: {
        dataSourceKey,
    },
});

/**
 * Akce pro oznámení chybné aktualizace dat.
 *
 * @param dataSourceKey Klíč pro identifikaci DataSource zadaný při inicializaci.
 * @param message Zpráva popisující chybu.
 */
export const createRefreshErrorAction = (dataSourceKey: string, message?: string): IRefreshDataErrorAction => ({
    type: REFRESH_DATA_ERROR,
    payload: {
        message,
    },
    meta: {
        dataSourceKey,
    },
});

/**
 * Akce pro aktualizaci fulltextové podmínky DataSource.
 *
 * @param dataSourceKey Klíč pro identifikaci DataSource zadaný při inicializaci.
 * @param newCondition Nová podmínka pro vyhledávání.
 * @param refreshOnChange Určuje, zda se při změně mají aktualizovat data.
 */
export const createUpdateFulltextDataSourceConditionAction = (
    dataSourceKey: string,
    newCondition: IFulltextSearchCondition,
    refreshOnChange: boolean = true,
): IUpdateFulltextDataSourceCondition => ({
    type: UPDATE_FULLTEXT_CONDITION,
    payload: {
        newCondition,
        refreshOnChange,
    },
    meta: {
        dataSourceKey,
    },
});

/**
 * Akce pro aktualizaci masterFrame podmínky DataSource
 *
 * @param dataSourceKey Klíč pro identifikaci DataSource zadaný při inicializaci.
 * @param newCondition Nová podmínka pro vyhledávání.
 * @param conditionParams Parametry pro podmínku.
 * @param isMasterRecordSelected Určuje, zda je vybrán záznam.
 * @param refreshOnChange Určuje, zda se při změně mají aktualizovat data.
 */
export const createUpdateMasterFrameDataSourceConditionAction = (
    dataSourceKey: string,
    newCondition: string,
    conditionParams: ConditionParams = null,
    isMasterRecordSelected?: boolean,
    refreshOnChange: boolean = true,
): IUpdateMasterFrameDataSourceCondition => ({
    type: UPDATE_MASTER_FRAME_CONDITION,
    payload: {
        newCondition,
        conditionParams,
        isMasterRecordSelected,
        refreshOnChange,
    },
    meta: {
        dataSourceKey,
    },
});

/**
 * Akce pro aktualizaci akcí pro záznam z MW.
 *
 * @param dataSourceKey Klíč pro identifikaci DataSource zadaný při inicializaci.
 */
export const createUpdateRecordActionsAction = (dataSourceKey: string): IUpdateRecordActionsAction => ({
    type: UPDATE_RECORD_ACTIONS,
    meta: {
        dataSourceKey,
    },
});

/**
 * Akce pro uložení aktualizovaných akcí pro záznam z MW.
 *
 * @param dataSourceKey Klíč pro identifikaci DataSource zadaný při inicializaci.
 * @param actions Nainicializované akce z MW.
 */
export const createUpdateRecordActionsSuccessAction = (
    dataSourceKey: string,
    actions: ReadonlyArray<ReactAction>,
): IUpdateRecordActionsSuccessAction => ({
    type: UPDATE_RECORD_ACTIONS_SUCCESS,
    payload: {
        actions,
    },
    meta: {
        dataSourceKey,
    },
});

/**
 * Akce, která nastavuje attributy v DataSource.
 *
 * @param dataSourceKey Klíč pro identifikaci DataSource zadaný při inicializaci.
 * @param attributes Nové atributy DataSource.
 */
export const createSetAttributesAction = (
    dataSourceKey: string,
    attributes: ReadonlyArray<IDataSourceAttribute>,
): ISetAttributesAction => ({
    type: SET_ATTRIBUTES,
    payload: {
        attributes,
    },
    meta: {
        dataSourceKey,
    },
});

/**
 * Akce pro nastavení řazení.
 *
 * @param dataSourceKey Klíč pro identifikaci DataSource zadaný při inicializaci.
 * @param orderBy
 */
export const createSetOrderByAction = (dataSourceKey: string, orderBy: ReadonlyArray<IOrderBy>): ISetOrderByAction => ({
    type: SET_ORDER_BY,
    payload: {
        orderBy,
    },
    meta: {
        dataSourceKey,
    },
});

/**
 * Akce pro přepnutí řazení u konkrétního atributu.
 *
 * @param dataSourceKey Klíč pro identifikaci DataSource zadaný při inicializaci.
 * @param attributeName Atribut, na kterém se má změnit řazení.
 * @param clearOthers Značí, zda se ostatní řazení smažou.
 * @param attributeName
 */
export const createToggleOrderByAction = (
    dataSourceKey: string,
    attributeName: string,
    clearOthers: boolean,
): IToggleOrderByAction => ({
    type: TOGGLE_ORDER_BY,
    payload: {
        attributeName,
        clearOthers,
    },
    meta: {
        dataSourceKey,
    },
});

/**
 * Akce pro nastavení režimu výběru více záznamů na datasource.
 *
 * @param dataSourceKey Klíč pro identifikaci DataSource zadaný při inicializaci.
 * @param multiselectMode
 */
export const createSetDatasourceMultiselectModeAction = (
    dataSourceKey: string,
    multiselectMode: boolean,
): ISetDatasourceMultiselectModeAction => ({
    type: SET_DATASOURCE_MULTISELECT_MODE,
    payload: {
        multiselectMode,
    },
    meta: {
        dataSourceKey,
    },
});

/**
 * Akce pro přepnutí označení(isChecked) na vybraných(isSelected) záznamech v DataSource.
 *
 * @param dataSourceKey Klíč pro identifikaci DataSource zadaný při inicializaci.
 */
export const createToggleCheckOnSelectedRecordsAction = (dataSourceKey: string): IToggleCheckOnSelectedRecordsAction => ({
    type: TOGGLE_CHECK_ON_SELECTED_RECORDS,
    meta: {
        dataSourceKey,
    },
});

/**
 * Akce pro označení / odznačení konkrétního záznamu v DataSource.
 *
 * @param dataSourceKey Klíč pro identifikaci DataSource zadaný při inicializaci.
 * @param recordId Identifikátor záznamu, který má být označen/odznačen.
 * @param ownerId Identifikátor nadřízeného záznamu, pokud je
 */
export const createToggleSelectedRecordCheckAction = (dataSourceKey: string, recordId: string, ownerId?: string): IToggleSelectedRecordCheckAction => ({
    type: TOGGLE_SELECTED_RECORD_CHECK,
    payload: {
        recordId,
        ownerId,
    },
    meta: {
        dataSourceKey,
    },
});

/**
 * Akce pro výběr všech načtených a přednačtených záznamu v DataSource.
 *
 * @param dataSourceKey Klíč pro identifikaci DataSource zadaný při inicializaci.
 * @param keepIncomingDataChecked Značí, zdali při načítání má zaškrtávat nová data
 */
export const createCheckAllRecordsAction = (dataSourceKey: string, keepIncomingDataChecked: boolean): ICheckAllRecordsAction => ({
    type: CHECK_ALL_RECORDS,
    payload: {
        keepIncomingDataChecked,
    },
    meta: {
        dataSourceKey,
    },
});

/**
 * Akce pro zaškrtnutí či odškrtnutí všech záznamů z Datasource
 *
 * @param dataSourceKey Klíč pro identifikaci DataSource zadaný při inicializaci.
 */
export const createToggleCheckAllRecordsAction = (dataSourceKey: string): IToggleCheckAllRecordsAction => ({
    type: TOGGLE_CHECK_ALL_RECORDS,
    meta: {
        dataSourceKey,
    },
});

/**
 * Akce pro výběr dalšího záznamu.
 *
 * @param dataSourceKey Klíč pro identifikaci DataSource zadaný při inicializaci.
 * @param clearOtherSelections Určuje, zda se při výběru dalšího záznamu smažou ostatní označení.
 */
export const createSelectNextRecord = (dataSourceKey: string, clearOtherSelections = false): ISelectNextRecord => ({
    type: SELECT_NEXT_RECORD,
    payload: {
        clearOtherSelections,
    },
    meta: {
        dataSourceKey,
    },
});

/**
 * Akce pro výběr předchozího záznamu.
 *
 * @param dataSourceKey Klíč pro identifikaci DataSource zadaný při inicializaci.
 * @param clearOtherSelections Určuje, zda se při výběru dalšího záznamu smažou ostatní označení.
 */
export const createSelectPreviousRecord = (
    dataSourceKey: string,
    clearOtherSelections: boolean,
): ISelectNextRecord => ({
    type: SELECT_PREVIOUS_RECORD,
    payload: {
        clearOtherSelections,
    },
    meta: {
        dataSourceKey,
    },
});

/**
 * Akce, která nastavuje informaci o tom, zda je na DataSource zakázáno načítání dat.
 *
 * @param dataSourceKey Klíč pro identifikaci DataSource zadaný při inicializaci.
 * @param value Značí, zda je zakázáno načítání dat.
 */
export const createSetDisableLoadingAction = (dataSourceKey: string, value: boolean): ISetDisableLoadingAction => ({
    type: SET_DISABLE_LOADING,
    payload: {
        value,
    },
    meta: {
        dataSourceKey,
    },
});

/**
 * Akce, která nastavuje informaci o tom, zda je možné otevřít detail záznamu.
 *
 * @param dataSourceKey Klíč pro identifikaci DataSource zadaný při inicializaci.
 * @param hasDetailForm Značí, zda je povoleno otevřít detail záznamu.
 */
export const createSetHasDetailFormAction = (dataSourceKey: string, hasDetailForm: boolean): ISetHasDetailFormAction => ({
    type: SET_HAS_DETAIL_FORM,
    payload: {
        hasDetailForm,
    },
    meta: {
        dataSourceKey,
    },
});

/**
 * Akce pro zrušení výběrů všech záznamů.
 * @param dataSourceKey Klíč pro identifikaci DataSource zadaný při inicializaci.
 */
export const createDeselectAllRecordsAction = (dataSourceKey: string): IDeselectAllRecordsAction => ({
    type: DESELECT_ALL_RECORDS,
    meta: {
        dataSourceKey,
    },
});

/**
 * Akce pro výběr záznamů daných identifikátory.
 *
 * @param dataSourceKey Klíč pro identifikaci DataSource zadaný při inicializaci.
 * @param selectedRecordsIds Pole identifikátorů.
 */
export const createSelectRecordsAction = (
    dataSourceKey: string,
    selectedRecordsIds: ReadonlyArray<string>,
): ISelectRecordsAction => ({
    type: SELECT_RECORDS,
    payload: {
        selectedRecordsIds,
    },
    meta: {
        dataSourceKey,
    },
});

/**
 * Akce pro výběr jednoho záznamu.
 *
 * @param dataSourceKey Klíč pro identifikaci DataSource zadaný při inicializaci.
 * @param recordId Identifikátor záznamu.
 */
export const createSelectRecordAction = (dataSourceKey: string, recordId: string): ISelectRecordAction => ({
    type: SELECT_RECORD,
    payload: {
        recordId,
    },
    meta: {
        dataSourceKey,
    },
});

/**
 * Akce pro odznačení všech záznamů.
 * @param dataSourceKey Klíč pro identifikaci DataSource zadaný při inicializaci.
 */
export const createUncheckAllRecordsAction = (dataSourceKey: string): IUncheckAllRecordsAction => ({
    type: UNCHECK_ALL_RECORDS,
    meta: {
        dataSourceKey,
    },
});

/**
 * Akce pro nastavení identifikátoru naposledy označeného záznamu.
 * @param dataSourceKey Klíč k instanci DataSource.
 * @param recordId Identifikátor označeného záznamu.
 */
export const createSetLastSelectedRecordIdAction = (dataSourceKey: string, recordId: string): ISetLastSelectedRecordIdAction => ({
    type: SET_LAST_SELECTED_RECORD_ID,
    payload: {
        recordId,
    },
    meta: {
        dataSourceKey,
    },
});

/**
 * Akce získání workflow akcí
 *
 * @param frameKey Klíč pro identifikaci framu zadaný při inicializaci.
 * @param recordId Identifikátor záznamu.
 */
export const createLoadWorkflowRequestAction = (dataSourceKey: string, recordId: string): ILoadWorkflowActionsAction => ({
    type: GET_WORKFLOW_ACTIONS_REQUEST,
    payload: {
        recordId,
    },
    meta: {
        dataSourceKey,
    },
});

/**
 * Akce pro uložení workflow akcí do storu
 * @param dataSourceKey Klíč k instanci DataSource.
 * @param workflowActions Jednotlivé workflow akce
 */
export const createLoadWorkflowSuccessAction = (dataSourceKey: string, workflowActions: ReadonlyArray<IWorkflowMenuItem>): ILoadWorkflowActionsSuccessAction => ({
    type: GET_WORKFLOW_ACTIONS_SUCCESS,
    payload: { workflowActions },
    meta: {
        dataSourceKey,
    },
});

/**
 * Akce pro neúspěšné načtení workflow akcí ze serveru. Nasetuje isLoading na false a nastaví workflow akce na prázdný objekt.
 * @param dataSourceKey Klíč k instanci DataSource.
 */
export const createLoadWorkflowErrorAction = (dataSourceKey: string, message: string): ILoadWorkflowActionsErrorAction => ({
    type: GET_WORKFLOW_ACTIONS_ERROR,
    payload: {
        message,
    },
    meta: {
        dataSourceKey,
    },
});

/**
 * Akce pro vyvolání workflow akce.
 * @param dataSourceKey Klíč k instanci DataSource.
 * @param workflowActionId Id vykonávané workflow akce.
 * @param recordId Id záznamu, pro který se má vykonat workflow akce.
*/
export const createExecuteWorkflowAction = (
    dataSourceKey: string,
    workflowActionId: string,
    recordId: string,
): IExecuteWorkflowAction => ({
    type: EXECUTE_WORKFLOW_ACTION,
    payload: {
        workflowActionId,
        recordId,
    },
    meta: {
        dataSourceKey,
    },
});

/**
 * Akce pro přídání pomocných kořenových záznamů, pro výpočet zaškrtávání záznaml s vnořenými záznamy
 * @param dataSourceKey Klíč pro identifikaci DataSource zadaný při inicializaci.
 * @param nodes kořenové záznamy obsahující děti či informaci o tom, zda nejsou v jiných kořenových záznamnech (eg. WFDialog -> GridParticipants)
 */
export const createAddRootNodes = (dataSourceKey: string, nodes: RootNodeTypedObject): ISetRootNodes => ({
    type: SET_ROOT_NODES,
    payload: {
        nodes,
    },
    meta: {
        dataSourceKey,
    },
});
